import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../../base/AdminContext';
import { Button } from 'primereact/button';
import { PanelMenu } from 'primereact/panelmenu';

interface AdminSidebarProps {
    visible: boolean;
    onToggle: () => void;
}

interface MenuItem {
    key: string;
    label: string;
    icon: string;
    path?: string;
    module: string;
    subModule?: string;
    children?: MenuItem[];
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ visible, onToggle }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { hasModuleAccess, checkPermission, admin, logout } = useAdmin();

    // State để quản lý các panel đang mở
    const [expandedKeys, setExpandedKeys] = useState<{ [key: string]: boolean }>({});

    // Định nghĩa menu items
    const menuItems: MenuItem[] = useMemo(() => [
        {
            key: 'dashboard',
            label: 'Dashboard',
            icon: 'pi pi-home',
            path: '/admin/dashboard',
            module: 'dashboard'
        },
        {
            key: 'trips',
            label: 'Quản lý chuyến đi',
            icon: 'pi pi-map',
            module: 'trip_management',
            children: [
                {
                    key: 'trips-list',
                    label: 'Danh sách chuyến đi',
                    icon: 'pi pi-list',
                    path: '/admin/trips',
                    module: 'trip_management',
                    subModule: 'trip_list'
                }
            ]
        },
        {
            key: 'drivers',
            label: 'Quản lý tài xế',
            icon: 'pi pi-users',
            module: 'driver_management',
            children: [
                {
                    key: 'drivers',
                    label: 'Danh sách tài xế',
                    icon: 'pi pi-clock',
                    path: '/admin/drivers',
                    module: 'driver_management',
                    subModule: 'drivers'
                },

            ]
        },
        {
            key: 'users',
            label: 'Quản lý người dùng',
            icon: 'pi pi-user-plus',
            module: 'user_management',
            children: [
                {
                    key: 'users-list',
                    label: 'Danh sách người dùng',
                    icon: 'pi pi-list',
                    path: '/admin/users',
                    module: 'user_management',
                    subModule: 'user_list'
                }
            ]
        },
        {
            key: 'notifications',
            label: 'Quản lý thông báo',
            icon: 'pi pi-bell',
            module: 'notification_management',
            children: [
                {
                    key: 'notifications-list',
                    label: 'Danh sách thông báo',
                    icon: 'pi pi-list',
                    path: '/admin/notifications',
                    module: 'notification_management',
                    subModule: 'notification_list'
                }
            ]
        },
        {
            key: 'vouchers',
            label: 'Quản lý Voucher',
            icon: 'pi pi-ticket',
            module: 'voucher_management',
            children: [
                {
                    key: 'vouchers-public',
                    label: 'Voucher chung',
                    icon: 'pi pi-gift',
                    path: '/admin/vouchers/public',
                    module: 'voucher_management',
                    subModule: 'vouchers_public'
                },

                {
                    key: 'vouchers-private',
                    label: 'Voucher riêng',
                    icon: 'pi pi-tag',
                    path: '/admin/vouchers/private',
                    module: 'voucher_management',
                    subModule: 'vouchers_private'
                },
                {
                    key: 'vouchers-reward',
                    label: 'Voucher đổi điểm',
                    icon: 'pi pi-star',
                    path: '/admin/vouchers/reward',
                    module: 'voucher_management',
                    subModule: 'vouchers_reward'
                },
                {
                    key: 'vouchers-random-code',
                    label: 'Voucher mã ngẫu nhiên',
                    icon: 'pi pi-qrcode',
                    path: '/admin/vouchers/random-code',
                    module: 'voucher_management',
                    subModule: 'vouchers_random_code'
                },

            ]
        },
        {
            key: 'permissions',
            label: 'Quản lý phân quyền',
            icon: 'pi pi-shield',
            module: 'permission_management',
            children: [
                {
                    key: 'roles-list',
                    label: 'Quản lý vai trò',
                    icon: 'pi pi-users',
                    path: '/admin/roles',
                    module: 'permission_management',
                    subModule: 'role_management'
                },
                {
                    key: 'permissions-list',
                    label: 'Danh sách quyền',
                    icon: 'pi pi-list',
                    path: '/admin/permissions',
                    module: 'permission_management',
                    subModule: 'permission_list'
                }
            ]
        },
        {
            key: 'admins',
            label: 'Quản lý admin',
            icon: 'pi pi-cog',
            module: 'admin_management',
            children: [
                {
                    key: 'admins-list',
                    label: 'Danh sách admin',
                    icon: 'pi pi-list',
                    path: '/admin/admins',
                    module: 'admin_management',
                    subModule: 'admin_list'
                }
            ]
        },
        {
            key: 'pricing',
            label: 'Quản lý giá cả',
            icon: 'pi pi-money-bill',
            module: 'pricing_management',
            children: [
                {
                    key: 'pricing-motorbike',
                    label: 'Giá xe máy',
                    icon: 'pi pi-circle',
                    path: '/admin/pricing/motorbike',
                    module: 'pricing_management',
                    subModule: 'pricing_motobike'
                },
                {
                    key: 'pricing-car-4',
                    label: 'Giá xe ô tô 4 chỗ',
                    icon: 'pi pi-car',
                    path: '/admin/pricing/car-4seat',
                    module: 'pricing_management',
                    subModule: 'pricing_car_4'
                },
                {
                    key: 'pricing-car-7',
                    label: 'Giá xe ô tô 7 chỗ',
                    icon: 'pi pi-truck',
                    path: '/admin/pricing/car-7seat',
                    module: 'pricing_management',
                    subModule: 'pricing_car_7'
                }
            ]
        },
        {
            key: 'news-banners-management',
            label: 'Quản lý tin tức & banner',
            icon: 'pi pi-image',
            module: 'news-banners-management',
            children: [
                {
                    key: 'banners-management',
                    label: 'Danh sách banner',
                    icon: 'pi pi-list',
                    path: '/admin/banners',
                    module: 'news-banners-management',
                    subModule: 'banners-management'
                },
                {
                    key: 'news-management',
                    label: 'Danh sách tin tức',
                    icon: 'pi pi-list',
                    path: '/admin/news',
                    module: 'news-banners-management',
                    subModule: 'news_management'
                }
            ]
        },
        {
            key: 'system_management',
            label: 'Quản lý hệ thống',
            icon: 'pi pi-cog',
            module: 'system_management',
            children: [
                {
                    key: 'system-configuration',
                    label: 'Cấu hình hệ thống',
                    icon: 'pi pi-wrench',
                    path: '/admin/system-configuration',
                    module: 'system_management',
                    subModule: 'system_configuration'
                }
            ]
        }
    ], []);

    // Function để tìm parent key của một path
    const findParentKeyByPath = useCallback((path: string): string | null => {
        for (const item of menuItems) {
            if (item.children) {
                for (const child of item.children) {
                    if (child.path === path) {
                        return item.key;
                    }
                }
            }
        }
        return null;
    }, [menuItems]);

    // Effect để tự động mở panel chứa item đang active
    useEffect(() => {
        const activeParentKey = findParentKeyByPath(location.pathname);
        if (activeParentKey) {
            setExpandedKeys(prev => ({
                ...prev,
                [activeParentKey]: true
            }));
        }
    }, [location.pathname, findParentKeyByPath]);

    // Filter menu items dựa trên quyền
    const filterMenuItems = (items: MenuItem[]): any[] => {
        return items.filter(item => {
            if (!hasModuleAccess(item.module)) {
                return false;
            }

            if (item.children) {
                const filteredChildren = filterMenuItems(item.children);
                if (filteredChildren.length === 0) return false;

                return {
                    ...item,
                    items: filteredChildren
                };
            }

            return checkPermission(item.module, item.subModule || null, 'view');
        }).map(item => {
            if (item.children) {
                return {
                    key: item.key,
                    label: item.label,
                    icon: item.icon,
                    expanded: expandedKeys[item.key] || false,
                    items: item.children.map((child: MenuItem) => ({
                        key: child.key,
                        label: child.label,
                        icon: child.icon,
                        command: () => {
                            if (child.path) {
                                navigate(child.path);
                            }
                        },
                        className: location.pathname === child.path ? 'active-menu-item' : ''
                    })),
                    command: () => {
                        // Toggle panel when header is clicked
                        setExpandedKeys(prev => ({
                            ...prev,
                            [item.key]: !prev[item.key]
                        }));
                    }
                };
            } else {
                return {
                    key: item.key,
                    label: item.label,
                    icon: item.icon,
                    command: () => {
                        if (item.path) {
                            navigate(item.path);
                        }
                    },
                    className: location.pathname === item.path ? 'active-menu-item' : ''
                };
            }
        });
    };

    const panelMenuItems = filterMenuItems([...menuItems]);

    const handleLogout = () => {
        if (window.confirm('Bạn có chắc chắn muốn đăng xuất?')) {
            logout();
        }
    };

    return (
        <div className="admin-sidebar h-full flex flex-column">
            {/* Sidebar Header */}
            <div className="sidebar-header p-4 border-bottom-1 surface-border">
                <div className="flex align-items-center justify-content-between">
                    <div className="flex align-items-center">
                        <i className="pi pi-car text-primary text-2xl mr-3"></i>
                        <h3 className="text-900 font-bold m-0">CMS Admin</h3>
                    </div>
                    <div className="flex gap-2">
                        {/* Toggle button for desktop */}
                        <Button
                            icon="pi pi-bars"
                            className="p-button-text p-button-sm hidden md:flex"
                            onClick={onToggle}
                        />
                        {/* Close button for mobile */}
                        <Button
                            icon="pi pi-times"
                            className="p-button-text p-button-sm md:hidden"
                            onClick={onToggle}
                        />
                    </div>
                </div>
            </div>

            {/* User Info */}
            <div className="user-info p-4 border-bottom-1 surface-border">
                <div className="flex align-items-center">
                    <div className="avatar-container mr-3">
                        <div className="w-3rem h-3rem bg-primary border-round-lg flex align-items-center justify-content-center">
                            <i className="pi pi-user text-white text-lg"></i>
                        </div>
                    </div>
                    <div className="flex-1">
                        <p className="text-900 font-semibold m-0 text-sm">{admin?.full_name}</p>
                        <p className="text-600 text-xs m-0">@{admin?.user_name}</p>
                    </div>
                </div>
            </div>

            {/* Navigation Menu */}
            <div className="flex-1 overflow-y-auto">
                <div className="p-2">
                    <PanelMenu
                        model={panelMenuItems}
                        className="w-full"
                        multiple={true}
                    />
                </div>
            </div>

            {/* Sidebar Footer */}
            <div className="sidebar-footer p-3 border-top-1 surface-border">
                <Button
                    icon="pi pi-sign-out"
                    label="Đăng xuất"
                    className="w-full p-button-outlined p-button-danger p-button-sm"
                    onClick={handleLogout}
                />
            </div>

            <style>{`
                .admin-sidebar {
                    background: #ffffff;
                    border-right: 1px solid #e5e7eb;
                    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
                }

                .sidebar-header h3 {
                    font-size: 1.25rem;
                }

                .user-info {
                    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                }

                .avatar-container {
                    position: relative;
                }

                .p-panelmenu .p-panelmenu-panel {
                    margin-bottom: 0.5rem;
                }

                .p-panelmenu .p-panelmenu-header-link {
                    padding: 0.75rem 1rem;
                    border-radius: 8px;
                    transition: all 0.3s ease;
                }

                .p-panelmenu .p-panelmenu-header-link:not(.p-disabled):hover {
                    background: #f8fafc;
                }

                .p-panelmenu .p-panelmenu-content {
                    border: none;
                    background: transparent;
                }

                .p-panelmenu .p-menuitem-link {
                    padding: 0.5rem 1rem 0.5rem 2.5rem;
                    border-radius: 6px;
                    transition: all 0.3s ease;
                    margin: 0.25rem 0;
                }

                .p-panelmenu .p-menuitem-link:not(.p-disabled):hover {
                    background: #f1f5f9;
                }

                .active-menu-item {
                    background: #e0f2fe !important;
                    color: #0277bd !important;
                    font-weight: 600;
                }

                .sidebar-footer .p-button {
                    justify-content: center;
                }

                /* Custom scrollbar */
                .overflow-y-auto::-webkit-scrollbar {
                    width: 6px;
                }

                .overflow-y-auto::-webkit-scrollbar-track {
                    background: #f1f5f9;
                }

                .overflow-y-auto::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 3px;
                }

                .overflow-y-auto::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }
            `}</style>
        </div>
    );
};

export default AdminSidebar;
